import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import AddCircle from 'mdi-react/AddCircleIcon';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';

import { RootState } from 'rootReducer';
import { DataGrid, DataGridColumn } from 'modules/shared/DataGrid';
import ModalWrapper from 'modules/shared/ModalWrapper';
import YesNoModal from 'modules/shared/YesNoModal';
import ErrorMsg from 'modules/shared/ErrorMsg';
import Loader from 'modules/kiosk/components/Loader';
import { useModal } from 'modules/shared/hooks';
import { DelayReason, DelayReasonRequest } from '../../../../entities/DelayReason';
import { tripStatuses } from '../../../../entities/Trip';
import {
  deleteDelayReasonRequest,
  getDelayReasonsError,
  getDelayReasonsRequest,
  saveDelayReasonRequest,
} from './services/actions';
import styles from './DelayReasonsTab.module.scss';

const statuses = [
  ['create', 'Новий'],
  ['parkingInLine', 'Виїзд у черзі'],
  ['waitingOrder', 'Очікування наряду'],
  ['waiting', 'Очікування завантаження'],
  ['downloading', 'Завантаження'],
  ['parking', 'Реєстрація на території'],
  ['waitingInvoice', 'Очікування накладної'],
  ['waitingDeparture', 'Очікування виїзду'],
  ['onPause', 'Пауза'],
  ['waitingAdmin', 'Очікування менеджера складу'],
  ['noOrder', 'Немає наряду'],
  ['unloading', 'Розвантаження'],
  ['parkingNoDocs', 'Без документів'],
  ['left', 'Виїхав'],
] as const;

const statusTitle = (statusId: number) => statuses.find(([key]) => tripStatuses[key] === statusId)?.[1] || statusId;

const DelayReasonsTabContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { delayReasons, loading, error } = useSelector(
    (state: RootState) => state.adiminModuleReducer.delayReasonsTabReducer,
  );
  const role = useSelector((state: RootState) => state.authModuleReducer.authReducer.role);
  const canManage = role?.name === 'ARM_ADMINISTRATOR' || role?.name === 'ARM_SUPERUSER';
  const [editedReason, setEditedReason] = useState<DelayReason | null>(null);
  const [form, setForm] = useState<DelayReasonRequest>({
    availableForStatusId: tripStatuses.waiting,
    commentText: '',
    isManual: false,
  });
  const [formError, setFormError] = useState('');
  const { isShow: isFormShown, openModal: openForm, closeModal: closeForm } = useModal();
  const { isShow: isDeleteShown, openModal: openDelete, closeModal: closeDelete } = useModal();
  const [selectedReason, setSelectedReason] = useState<DelayReason | null>(null);

  useEffect(() => {
    dispatch(getDelayReasonsRequest());
  }, []);

  const startCreate = () => {
    setEditedReason(null);
    setForm({ availableForStatusId: tripStatuses.waiting, commentText: '', isManual: false });
    setFormError('');
    openForm();
  };

  const startEdit = (reason: DelayReason) => {
    setEditedReason(reason);
    setForm({
      id: reason.id,
      availableForStatusId: reason.availableForStatusId || tripStatuses.waiting,
      commentText: reason.commentText,
      isManual: reason.isManual === true || reason.isManual === 1,
    });
    setFormError('');
    openForm();
  };

  const submit = () => {
    if (!form.commentText.trim()) {
      setFormError('Вкажіть текст причини');
      return;
    }
    closeForm();
    dispatch(
      saveDelayReasonRequest({ ...form, commentText: form.commentText.trim(), isManual: Boolean(form.isManual) }),
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        {canManage && (
          <Button variant="light" className="mr-2" onClick={startCreate}>
            <AddCircle className={styles.plusIcon} /> Створити причину
          </Button>
        )}
        {canManage && (
          <Button variant="light" className="mr-2" onClick={openDelete} disabled={!selectedReason}>
            <DeleteIcon className={styles.dangerIcon} /> Видалити причину
          </Button>
        )}
      </div>

      {loading && <Loader />}
      <DataGrid
        data={delayReasons}
        onRow={(row) => {
          const reason = row as DelayReason;
          return {
            onDoubleClick: canManage ? () => startEdit(reason) : undefined,
            onClick: () => setSelectedReason(reason),
            style: {
              backgroundColor: reason.id === selectedReason?.id ? '#add8e6' : '',
            },
          };
        }}
      >
        <DataGridColumn
          title="Статус"
          dataIndex="availableForStatusId"
          key="status"
          render={(value) => statusTitle(value)}
        />
        <DataGridColumn title="Причина" dataIndex="commentText" key="commentText" />
        <DataGridColumn
          title="Ручний коментар"
          dataIndex="isManual"
          key="isManual"
          render={(value) => (value === true || value === 1 ? 'Так' : 'Ні')}
        />
      </DataGrid>

      <ErrorMsg
        isShow={!!error}
        error={error?.message || 'Помилка при отриманні причин затримки'}
        closeHandler={() => dispatch(getDelayReasonsError(null))}
      />

      <ModalWrapper
        show={isFormShown}
        onHide={closeForm}
        title={editedReason ? 'Редагування причини' : 'Створення причини'}
      >
        <Form.Group>
          <Form.Label>Статус</Form.Label>
          <Form.Control
            as="select"
            value={form.availableForStatusId}
            onChange={(event) => setForm({ ...form, availableForStatusId: Number(event.target.value) })}
          >
            {statuses.map(([key, title]) => (
              <option key={key} value={tripStatuses[key]}>
                {title}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group>
          <Form.Label>Текст причини</Form.Label>
          <Form.Control
            value={form.commentText}
            onChange={(event) => setForm({ ...form, commentText: event.target.value })}
            maxLength={500}
          />
        </Form.Group>
        <Form.Check
          type="checkbox"
          label="Потрібен ручний коментар"
          checked={Boolean(form.isManual)}
          onChange={(event) => setForm({ ...form, isManual: event.target.checked })}
        />
        {formError && <div className="text-danger mt-2">{formError}</div>}
        <div className="d-flex justify-content-around mt-4">
          <Button variant="outline-secondary" onClick={closeForm}>
            Скасувати
          </Button>
          <Button variant="outline-primary" onClick={submit}>
            Зберегти
          </Button>
        </div>
      </ModalWrapper>

      <YesNoModal
        isShow={isDeleteShown}
        title="Видалити причину затримки?"
        closeModal={closeDelete}
        acceptHandler={() => {
          closeDelete();
          if (selectedReason) dispatch(deleteDelayReasonRequest(selectedReason.id));
        }}
      />
    </div>
  );
};

export default DelayReasonsTabContainer;
